(function () {
  'use strict';

  if (window.AxiomHeadingReveal) return;

  var HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [data-heading-reveal]';
  // Theme settings > Motion > Word reveal: Large headings splits only these type styles. Smaller
  // headings (card, quote and accordion titles) are left whole and come in with their content.
  var LARGE_HEADING_SELECTOR = '.type-style--display, .type-style--page-title, .type-style--section-heading';
  var WORD_CLASS = 'heading-reveal__word';
  var INNER_CLASS = 'heading-reveal__inner';
  var BASE_DELAY = 250;
  var WORD_STAGGER = 30;
  var observedHeadings = new Set();
  var bootObserver = null;
  var revealObserver = null;
  var mutationObserver = null;

  function prefersReducedMotion() {
    return window.Axiom && typeof window.Axiom.prefersReducedMotion === 'function'
      ? window.Axiom.prefersReducedMotion()
      : window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isEnabled() {
    return document.body
      && document.body.dataset.headingRevealAnimation === 'true'
      && !prefersReducedMotion();
  }

  function isInScope(heading) {
    if (document.body.dataset.headingRevealScope !== 'large') return true;
    return heading.matches(LARGE_HEADING_SELECTOR) || Boolean(heading.querySelector(LARGE_HEADING_SELECTOR));
  }

  function isHeading(element) {
    return element instanceof Element
      && (/^H[1-6]$/.test(element.tagName) || element.hasAttribute('data-heading-reveal'));
  }

  function shouldSkipTextNode(node, heading) {
    var parent = node.parentElement;
    if (!parent || !node.nodeValue || !node.nodeValue.trim()) return true;
    if (parent.closest('svg, script, style, template, .visually-hidden, [aria-hidden="true"], [data-heading-reveal-ignore]')) return true;
    return parent.closest('.' + INNER_CLASS) && heading.contains(parent);
  }

  function getTextNodes(heading) {
    var nodes = [];
    var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return shouldSkipTextNode(node, heading) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });
    var currentNode = walker.nextNode();
    while (currentNode) {
      nodes.push(currentNode);
      currentNode = walker.nextNode();
    }
    return nodes;
  }

  function splitTextNode(node, startIndex) {
    var parts = node.nodeValue.match(/\s+|[^\s]+/g) || [];
    var fragment = document.createDocumentFragment();
    var wordIndex = startIndex;

    parts.forEach(function (part) {
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(part));
        return;
      }

      var word = document.createElement('span');
      var inner = document.createElement('span');
      word.className = WORD_CLASS;
      inner.className = INNER_CLASS;
      inner.textContent = part;
      inner.style.setProperty('--heading-reveal-delay', (BASE_DELAY + wordIndex * WORD_STAGGER) + 'ms');
      word.appendChild(inner);
      fragment.appendChild(word);
      wordIndex += 1;
    });

    node.replaceWith(fragment);
    return wordIndex;
  }

  function activateHeading(heading, immediate) {
    if (!heading) return;
    if (bootObserver) bootObserver.unobserve(heading);
    if (revealObserver) revealObserver.unobserve(heading);
    observedHeadings.delete(heading);
    heading.dataset.headingRevealState = 'active';

    if (immediate) {
      heading.querySelectorAll('.' + INNER_CLASS).forEach(function (inner) {
        inner.style.animationDelay = '0ms';
        inner.style.animationDuration = '0ms';
      });
    }
  }

  function splitHeading(heading) {
    var wordIndex = 0;
    getTextNodes(heading).forEach(function (node) {
      wordIndex = splitTextNode(node, wordIndex);
    });
    if (wordIndex) heading.classList.add('heading-reveal');
    return wordIndex > 0;
  }

  // A heading below the screen at boot is split into words and hidden until it scrolls in.
  function holdHeading(heading) {
    if (!splitHeading(heading)) {
      heading.dataset.headingRevealState = 'static';
      return;
    }
    heading.dataset.headingRevealState = 'inactive';
    observedHeadings.add(heading);
    ensureRevealObserver().observe(heading);
  }

  // The script runs after first paint, so a heading already on screen (or scrolled past, or not
  // rendered) has been seen: it stays as it is instead of disappearing and animating in again.
  // The observer reports each position after layout, so booting never forces a layout, and the
  // headings that stay are never split.
  function ensureBootObserver() {
    if (bootObserver) return bootObserver;
    bootObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var heading = entry.target;
        bootObserver.unobserve(heading);
        if (heading.dataset.headingRevealState !== 'pending') return;
        if (entry.isIntersecting || entry.boundingClientRect.bottom <= 0) {
          heading.dataset.headingRevealState = 'static';
          return;
        }
        holdHeading(heading);
      });
    }, { threshold: 0 });
    return bootObserver;
  }

  function ensureRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activateHeading(entry.target, false);
        } else if (entry.boundingClientRect.bottom <= 0) {
          // Already scrolled past, or not rendered (zero rect): show without animating.
          activateHeading(entry.target, true);
        }
      });
    }, {
      // The same trigger line as the section content reveal (global.js), so a heading starts
      // with (not after) the text below it.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    });
    return revealObserver;
  }

  function initHeading(heading) {
    if (!isEnabled() || !isHeading(heading) || !heading.closest('.shopify-section')) return;
    if (heading.matches('.visually-hidden, [aria-hidden="true"], [data-heading-reveal-ignore]')) return;
    if (heading.dataset.headingRevealInitialized === 'true') return;
    if (!getTextNodes(heading).length) return;

    heading.dataset.headingRevealInitialized = 'true';

    // Manual headings (product titles in Banner sticky scrolling) are revealed by their component.
    if (heading.hasAttribute('data-heading-reveal-manual')) {
      if (!splitHeading(heading)) {
        delete heading.dataset.headingRevealInitialized;
        return;
      }
      heading.dataset.headingRevealState = 'inactive';
      return;
    }

    if (!isInScope(heading)) return;

    if (!('IntersectionObserver' in window)) {
      heading.dataset.headingRevealState = 'static';
      return;
    }

    heading.dataset.headingRevealState = 'pending';
    ensureBootObserver().observe(heading);
  }

  function resetHeading(heading) {
    if (!heading) return;
    initHeading(heading);
    if (bootObserver) bootObserver.unobserve(heading);
    if (revealObserver) revealObserver.unobserve(heading);
    observedHeadings.delete(heading);
    heading.dataset.headingRevealState = 'inactive';
    heading.querySelectorAll('.' + INNER_CLASS).forEach(function (inner) {
      inner.style.removeProperty('animation-delay');
      inner.style.removeProperty('animation-duration');
    });
  }

  function collectHeadings(scope) {
    var headings = [];
    if (isHeading(scope) && scope.closest('.shopify-section')) headings.push(scope);
    if (scope && scope.querySelectorAll) {
      scope.querySelectorAll(HEADING_SELECTOR).forEach(function (heading) {
        headings.push(heading);
      });
    }
    return headings;
  }

  function initScope(scope) {
    if (!isEnabled()) return;
    collectHeadings(scope || document).forEach(initHeading);
  }

  function cleanupScope(scope) {
    collectHeadings(scope || document).forEach(function (heading) {
      if (bootObserver) bootObserver.unobserve(heading);
      if (revealObserver) revealObserver.unobserve(heading);
      observedHeadings.delete(heading);
    });
  }

  function handleMutations(mutations) {
    mutations.forEach(function (mutation) {
      mutation.removedNodes.forEach(function (node) {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        cleanupScope(node);
      });

      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        initScope(node);
      });

      // A split heading whose text was replaced is split again; a static one simply shows its new text.
      var target = mutation.target;
      var splitState = target.dataset && (target.dataset.headingRevealState === 'inactive' || target.dataset.headingRevealState === 'active');
      if (isHeading(target) && target.dataset.headingRevealInitialized === 'true' && splitState && !target.querySelector('.' + WORD_CLASS)) {
        delete target.dataset.headingRevealInitialized;
        delete target.dataset.headingRevealState;
        initHeading(target);
      }
    });
  }

  function revealSelectedBlock(event) {
    collectHeadings(event.target).forEach(function (heading) {
      initHeading(heading);
      activateHeading(heading, true);
    });
  }

  function init() {
    if (!isEnabled()) return;
    initScope(document);
    mutationObserver = new MutationObserver(handleMutations);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initScope(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    cleanupScope(event.target);
  });
  document.addEventListener('shopify:block:select', revealSelectedBlock);
  document.addEventListener('axiom:section-rendered', function (event) {
    initScope(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.AxiomHeadingReveal = {
    init: initScope,
    reveal: function (heading) {
      initHeading(heading);
      activateHeading(heading, false);
    },
    reset: resetHeading
  };
})();
